import { defineStore } from 'pinia'
import apiService from '@/api'
import { useAuthStore } from '@/features/account/session'
import type { Bot } from '@/types/domain'
import type { BotCreateData, BotUpdateData } from '@/api/contract'

interface BotsState {
  accountId: string | null
  generation: number
  catalogVersion: number
  bots: Bot[]
  loading: boolean
  error: string | null
  botsLastFetched: number | null
  cacheDuration: number
}

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message
  if (error && typeof error === 'object' && 'message' in error && typeof (error as { message?: unknown }).message === 'string') {
    return (error as { message: string }).message
  }
  return 'Unknown error'
}

export const useBotsStore = defineStore('bots', {
  state: (): BotsState => ({
    accountId: null,
    generation: 0,
    catalogVersion: 0,
    bots: [],
    loading: false,
    error: null,
    // Caching state
    botsLastFetched: null,
    cacheDuration: 5 * 60 * 1000, // 5 minutes in milliseconds
  }),

  getters: {
    getBotById: (state) => (id: number | string): Bot | undefined => {
      return state.bots.find(bot => String(bot.id) === String(id))
    },

    getUserBots: (state): Bot[] => {
      const authStore = useAuthStore()
      return state.bots.filter(bot => bot.user_id === authStore.userId)
    },

    defaultBot(): Bot | null {
      return this.getUserBots.find(bot => bot.is_default) ?? null
    },
    fallbackBot(): Bot | null {
      return this.defaultBot ?? this.getUserBots[0] ?? null
    },

  },

  actions: {
    setAccount(id: string | null) {
      if (this.accountId === id) return
      this.accountId = id
      this.generation++
      this.catalogVersion++
      this.bots = []
      this.botsLastFetched = null
      this.loading = false
      this.error = null
    },
    // Helper to get user ID from auth store
    _getUserId(): string | null {
      const authStore = useAuthStore()
      return authStore.userId
    },

    // Bot/API settings actions
    async fetchBots(forceRefresh = false) {
      this.setAccount(this._getUserId())
      if (!this.accountId) return []
      const generation = this.generation
      // Check cache first unless forced refresh
      const now = Date.now()
      if (!forceRefresh && this.botsLastFetched && (now - this.botsLastFetched) < this.cacheDuration) {
        console.log('[BotsStore] Using cached bots data')
        return this.bots
      }

      const request = ++this.catalogVersion
      this.loading = true
      this.error = null
      try {
        const response = await apiService.getBots()
        if (generation !== this.generation || request !== this.catalogVersion || this.accountId !== this._getUserId()) return []
        this.bots = response.bots || []
        this.botsLastFetched = now // Update cache timestamp

        return this.bots
      } catch (error: unknown) {
        if (generation !== this.generation || request !== this.catalogVersion) return []
        this.error = getErrorMessage(error)
        console.error('Failed to fetch bots:', error)
        throw error
      } finally {
        if (generation === this.generation && request === this.catalogVersion) this.loading = false
      }
    },

    async createBot(botData: BotCreateData) {
      this.setAccount(this._getUserId())
      const generation = this.generation
      this.loading = true
      this.error = null
      try {
        // Check if this is the first bot for the user
        const userBots = this.getUserBots
        const isFirstBot = userBots.length === 0

        // Set as default if it's the first bot
        const userId = this._getUserId() ?? ''
        const botWithUser = {
          ...botData,
          user_id: userId,
          is_default: isFirstBot
        }

        await apiService.createBot(botWithUser)
        if (generation !== this.generation || this.accountId !== this._getUserId()) return null
        const bots = await this.fetchBots(true)
        const matchingBots = (bots || []).filter((bot: Bot) => (
          bot.user_id === userId &&
          bot.model === botWithUser.model &&
          bot.access_path === botWithUser.access_path &&
          (bot.name || '') === (botWithUser.name || '')
        ))
        const createdBot = matchingBots.sort((a: Bot, b: Bot) => {
          const aTime = new Date(a.created_at || 0).getTime()
          const bTime = new Date(b.created_at || 0).getTime()
          return bTime - aTime
        })[0] || null
        return createdBot
      } catch (error: unknown) {
        if (generation !== this.generation) return null
        this.error = getErrorMessage(error)
        console.error('Failed to create bot:', error)
        throw error
      } finally {
        if (generation === this.generation) this.loading = false
      }
    },

    async updateBot(id: number | string, botData: BotUpdateData) {
      this.setAccount(this._getUserId())
      const generation = this.generation
      this.loading = true
      this.error = null
      try {
        const userId = this._getUserId() ?? ''
        const botWithUser = { ...botData, user_id: userId }
        await apiService.updateBot(id, botWithUser)
        if (generation !== this.generation || this.accountId !== this._getUserId()) return null
        this.catalogVersion++

        // Update in local state
        const index = this.bots.findIndex(bot => String(bot.id) === String(id))
        if (index !== -1) {
          this.bots[index] = { ...this.bots[index], ...botWithUser }
        }

      } catch (error: unknown) {
        if (generation !== this.generation) return null
        this.error = getErrorMessage(error)
        console.error('Failed to update bot:', error)
        throw error
      } finally {
        if (generation === this.generation) this.loading = false
      }
    },

    async deleteBot(id: number | string) {
      this.setAccount(this._getUserId())
      const generation = this.generation
      this.loading = true
      this.error = null
      try {
        await apiService.deleteBot(id)
        if (generation !== this.generation || this.accountId !== this._getUserId()) return null
        this.catalogVersion++

        // Remove from local state
        this.bots = this.bots.filter(bot => String(bot.id) !== String(id))

      } catch (error: unknown) {
        if (generation !== this.generation) return null
        this.error = getErrorMessage(error)
        console.error('Failed to delete bot:', error)
        throw error
      } finally {
        if (generation === this.generation) this.loading = false
      }
    },

    async setDefaultBot(id: number | string) {
      this.setAccount(this._getUserId())
      const generation = this.generation
      this.loading = true
      this.error = null

      // return early if the bot is already default
      if (this.defaultBot && String(this.defaultBot.id) === String(id) && this.defaultBot.is_default) {
        if (generation === this.generation) this.loading = false
        return
      }

      try {
        await apiService.setDefaultBot(id)
        if (generation !== this.generation || this.accountId !== this._getUserId()) return null
        this.catalogVersion++

        // Update local state: set the specified bot as default and unset others
        this.bots = this.bots.map(bot => ({
          ...bot,
          is_default: String(bot.id) === String(id)
        }))

      } catch (error: unknown) {
        if (generation !== this.generation) return null
        this.error = getErrorMessage(error)
        // The existing API performs two writes. Reconcile a partial failure
        // before showing an account default as authoritative again.
        await this.fetchBots(true).catch(() => {})
        if (generation !== this.generation) return null
        this.error = getErrorMessage(error)
        console.error('Failed to set default bot:', error)
        throw error
      } finally {
        if (generation === this.generation) this.loading = false
      }
    },

    clearError() { this.error = null },

    mergeBotsFromContext(bots: Bot[]) {
      this.setAccount(this._getUserId())
      this.catalogVersion++
      this.bots = bots
      this.botsLastFetched = Date.now()
      this.loading = false
    },
  },
})

